import { useCallback } from 'react';

import { useFeatureFlags, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

export const INITIAL_SCAN_TYPE_LABEL = 'Scope';
export const CHANGE_ORDER_SCAN_TYPE_LABEL = 'Change Order';
export const CLOSE_OUT_SCAN_TYPE_LABEL = 'Closeout';

export const useScopeSubmissionAction = () => {
  const { featureFlags } = useFeatureFlags();
  const { scan, scanMutators, scanIsEditable, scanIsSubmittable, effectiveScanType } = useScanEditingContextSlice([
    'scan',
    'scanMutators',
    'scanIsEditable',
    'scanIsSubmittable',
    'effectiveScanType',
  ]);

  // Mirrors ScopeToolbar.tsx's submitDoc derivation: only these three scan
  // types have a submit action at all, so canSubmit stays false for the
  // 'progress'/'progressRework' types effectiveScanType can also report.
  const scanTypeLabel = effectiveScanType === 'initial'
    ? INITIAL_SCAN_TYPE_LABEL
    : effectiveScanType === 'changeRequest'
    ? CHANGE_ORDER_SCAN_TYPE_LABEL
    : effectiveScanType === 'progressCompleted'
    ? CLOSE_OUT_SCAN_TYPE_LABEL
    : undefined;

  const canEditCurrentProjectWork = !featureFlags.turnBasedHandoff || scanIsEditable;
  const canSubmit = canEditCurrentProjectWork && scanIsSubmittable && !!scanTypeLabel;
  // Turn-based projects hand every reviewable stage to the next holder;
  // Closeout is approved directly by the current designated Project Manager.
  const turnBasedHandoffReplacesSubmission = featureFlags.turnBasedHandoff;

  // Ordinary work in progress toward an eventual closeout (scope or change
  // order already approved, no data yet needing review): the next real
  // action here is submitting closeout, just not yet - the header action
  // previews that button disabled instead of disappearing entirely, so it
  // doesn't pop into existence for the first time only once work completes.
  const showDisabledCloseoutPreview = effectiveScanType === 'progress' || effectiveScanType === 'progressRework';

  const submitForReview = useCallback(async () => {
    if (!scan || !canEditCurrentProjectWork) return;
    await scanMutators.update(scan.id, { scanLifecycleStatus: 'pendingReview' });
    trackEvent(ANALYTICS_EVENTS.scanSubmittedForReview, { scanId: scan.id });
  }, [canEditCurrentProjectWork, scan, scanMutators]);

  return { canSubmit, scanTypeLabel, showDisabledCloseoutPreview, submitForReview, turnBasedHandoffReplacesSubmission };
};
